import type { IAuthUser } from '@atlas/types'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useAuth as useOidcAuth } from 'react-oidc-context'
import { isBackendUnavailable } from '@/auth/auth-error'
import { useLogout } from '@/auth/use-logout'
import { authService } from '@/services/auth.service'

/**
 * Resolution of the Atlas identity for a valid OIDC session:
 * - `unauthenticated`: no valid OIDC user, or `/users/auth/me` was definitively
 *   rejected (401 exhausted / 403) — the existing login-redirect path applies.
 * - `authenticated`: `/users/auth/me` resolved the Atlas user.
 * - `backend-unavailable`: `/users/auth/me` failed on a network error or 5xx — the
 *   OIDC session is still valid, but the Atlas identity could not be resolved.
 */
export type AuthStatus = 'unauthenticated' | 'authenticated' | 'backend-unavailable'

export interface UseAuthResult {
	user: IAuthUser | null
	isAuthenticated: boolean
	isLoading: boolean
	/** Discriminated resolution state — see {@link AuthStatus}. */
	status: AuthStatus
	/** Provider or `/users/auth/me` error; optional so existing mocks stay valid. */
	error?: unknown
	login: () => void
	logout: () => void
	/** Refetch `/users/auth/me` without touching the OIDC session. */
	retryAuthMe: () => void
}

/**
 * Auth adapter over (1) the Cognito OIDC provider state and (2) a React Query
 * `GET /users/auth/me` (ADR-0012).
 *
 * The OIDC user proves external identity ONLY; the Atlas `/users/auth/me` response is
 * the authoritative source of the profile, role, and active/provisioned state.
 * The app is "fully authenticated" only when the OIDC user is valid AND `/users/auth/me`
 * has succeeded. Role is never read from Cognito claims; no local user is created
 * from the browser. There is no bypass — the real Cognito flow is always used.
 */
export function useAuth(): UseAuthResult {
	const oidc = useOidcAuth()
	const logout = useLogout()
	const hasValidOidcUser = !!oidc.user && !oidc.user.expired

	const authMe = useQuery({
		queryKey: ['auth', 'me'],
		enabled: hasValidOidcUser,
		queryFn: () => authService.getCurrentUser(oidc.user?.access_token ?? ''),
		retry: 1,
		staleTime: 5 * 60 * 1000,
	})

	const backendUnavailable =
		hasValidOidcUser &&
		authMe.isError &&
		isAxiosError(authMe.error) &&
		isBackendUnavailable(authMe.error.response?.status)

	const status: AuthStatus = !hasValidOidcUser
		? 'unauthenticated'
		: backendUnavailable
			? 'backend-unavailable'
			: authMe.isSuccess
				? 'authenticated'
				: 'unauthenticated'

	return {
		user: authMe.data ?? null,
		isAuthenticated: hasValidOidcUser && authMe.isSuccess,
		isLoading: oidc.isLoading || (hasValidOidcUser && authMe.isLoading),
		status,
		error: oidc.error ?? (authMe.isError ? authMe.error : undefined),
		login: () => {
			void oidc.signinRedirect()
		},
		// Explicit Cognito logout: clear Atlas state → remove OIDC user →
		// redirect to the Cognito Hosted UI `/logout` built only from validated config.
		logout,
		retryAuthMe: () => {
			void authMe.refetch()
		},
	}
}
